import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function FailedReloadsPage() {
  const [failedSubs, setFailedSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState({});
  const navigate = useNavigate();

  const fetchFailedReloads = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/recurring-subscriptions/failed-reloads`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFailedSubs(res.data.failed_subscriptions);
    } catch (error) {
      console.error('Error fetching failed reloads', error);
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFailedReloads();
  }, []);

  const handleRetry = async (subscriptionId) => {
    try {
      setRetrying(prev => ({ ...prev, [subscriptionId]: true }));
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/recurring-subscriptions/${subscriptionId}/retry-reload`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.data.success) {
        toast.success('Auto-Reload erfolgreich durchgeführt!');
        fetchFailedReloads(); // Aktualisiere Liste
      } else {
        toast.error(`Fehler: ${res.data.message}`);
      }
    } catch (error) {
      console.error('Error retrying reload', error);
      toast.error('Fehler beim erneuten Versuch');
    } finally {
      setRetrying(prev => ({ ...prev, [subscriptionId]: false }));
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-4">Abos mit Zahlungsproblemen</h1>
        <p className="text-gray-600">Lädt...</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Abos mit Zahlungsproblemen</h1>
          <p className="text-gray-600">{failedSubs.length} Abo(s) mit fehlgeschlagenem Auto-Reload</p>
        </div>
        <Button onClick={() => navigate('/dashboard')} variant="outline">
          Zurück
        </Button>
      </div>

      {/* Info Box für Verständnis */}
      <Card className="mb-6 border-blue-200 bg-blue-50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <span className="text-2xl">ℹ️</span>
            <div>
              <h3 className="font-semibold text-blue-900 mb-2">Was ist Auto-Reload?</h3>
              <p className="text-sm text-blue-800 mb-2">
                Bei Abos mit automatischer Aufladung versucht das System, die Kreditkarte des Kunden automatisch zu belasten, 
                wenn die Punkte aufgebraucht sind oder der Abrechnungsmonat erreicht ist.
              </p>
              <p className="text-sm text-blue-800">
                <strong>Was passiert bei einem Fehler?</strong> Das System versucht es <strong>3x</strong>. 
                Nach dem 3. Fehlversuch wird das Abo auf "Reload fehlgeschlagen" gesetzt und der Kunde + Admin werden benachrichtigt.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {failedSubs.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-center text-gray-500">
            ✅ Keine Probleme - Alle Abos funktionieren einwandfrei!
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {failedSubs.map((sub) => {
            const statusConfig = {
              failed_reload: { label: 'Reload fehlgeschlagen', className: 'bg-red-100 text-red-800' },
              active: { label: 'Aktiv (Fehler)', className: 'bg-yellow-100 text-yellow-800' },
            };
            const statusCfg = statusConfig[sub.status] || statusConfig.active;

            return (
              <Card key={sub.id} className="border-2 border-red-200">
                <CardHeader className="bg-red-50">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg">{sub.name}</CardTitle>
                      <p className="text-sm text-gray-600 mt-1">
                        Kunde: {sub.customer_name || 'Unbekannt'} ({sub.customer_email})
                      </p>
                    </div>
                    <Badge className={statusCfg.className}>{statusCfg.label}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-sm font-semibold text-gray-700">Fehlversuche:</p>
                      <p className="text-base">{sub.failed_reload_count || 0} / 3</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-700">Letzter Fehler:</p>
                      <p className="text-base">
                        {sub.last_failed_reload_date 
                          ? new Date(sub.last_failed_reload_date).toLocaleString('de-CH')
                          : '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-700">Betrag:</p>
                      <p className="text-base">CHF {sub.price_per_month?.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-700">Nächster Retry:</p>
                      <p className="text-base">
                        {sub.next_retry_date 
                          ? new Date(sub.next_retry_date).toLocaleDateString('de-CH')
                          : 'Kein automatischer Retry'}
                      </p>
                    </div>
                  </div>

                  {sub.last_failed_reload_reason && (
                    <div className="mb-4 p-3 bg-red-50 rounded border border-red-200">
                      <p className="text-sm font-semibold text-red-900 mb-1">Fehlergrund:</p>
                      <p className="text-sm text-red-700 mb-2">{sub.last_failed_reload_reason}</p>
                      
                      {/* Benutzerfreundliche Erklärung */}
                      {sub.last_failed_reload_reason.toLowerCase().includes('insufficient funds') && (
                        <div className="mt-2 p-2 bg-blue-50 rounded border border-blue-200">
                          <p className="text-sm text-blue-900">
                            <strong>💡 Was bedeutet das?</strong><br/>
                            Die Kreditkarte des Kunden hat nicht genügend Deckung. Die automatische Aufladung konnte nicht durchgeführt werden.
                          </p>
                          <p className="text-sm text-blue-800 mt-2">
                            <strong>✅ Lösungen:</strong>
                          </p>
                          <ul className="text-sm text-blue-800 list-disc ml-5 mt-1">
                            <li>Kunde bitten, die Kreditkarte aufzuladen</li>
                            <li>Alternative Zahlungsmethode hinterlegen</li>
                            <li>Nach Behebung: "Erneut versuchen" klicken</li>
                          </ul>
                        </div>
                      )}
                      
                      {sub.last_failed_reload_reason.toLowerCase().includes('card') && 
                       !sub.last_failed_reload_reason.toLowerCase().includes('insufficient funds') && (
                        <div className="mt-2 p-2 bg-blue-50 rounded border border-blue-200">
                          <p className="text-sm text-blue-900">
                            <strong>💡 Was bedeutet das?</strong><br/>
                            Es gab ein Problem mit der Kreditkarte (z.B. abgelaufen, gesperrt oder ungültig).
                          </p>
                          <p className="text-sm text-blue-800 mt-2">
                            <strong>✅ Lösung:</strong> Kunde bitten, eine neue/gültige Zahlungsmethode zu hinterlegen.
                          </p>
                        </div>
                      )}
                      
                      {sub.last_failed_reload_reason.toLowerCase().includes('declined') && (
                        <div className="mt-2 p-2 bg-blue-50 rounded border border-blue-200">
                          <p className="text-sm text-blue-900">
                            <strong>💡 Was bedeutet das?</strong><br/>
                            Die Bank oder das Kreditkartenunternehmen hat die Zahlung abgelehnt.
                          </p>
                          <p className="text-sm text-blue-800 mt-2">
                            <strong>✅ Lösung:</strong> Kunde sollte sich mit seiner Bank/Kreditkartenfirma in Verbindung setzen.
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleRetry(sub.id)}
                      disabled={retrying[sub.id]}
                      className="flex-1"
                    >
                      {retrying[sub.id] ? '⏳ Wird verarbeitet...' : '🔄 Erneut versuchen'}
                    </Button>
                    <Button
                      onClick={() => navigate(`/customers`)}
                      variant="outline"
                      className="flex-1"
                    >
                      👤 Kunde kontaktieren
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default FailedReloadsPage;
